import { TollAreaStatistics } from "@/core/repos/tables/toll-area.repo";
import React, { useEffect, useState } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  PieSeries,
} from "@syncfusion/ej2-react-charts";
import { StatsPieChart } from "./StatsPieChart";
import { Col, Card, Row } from "antd";
import { formatCurrencyPipeText } from "@/core/pipes/format-currency.pipe";
import { StatsBarChart } from "./StatsBarChart";
import { title } from "process";
import { SummaryCards } from "./SummaryCards";
import { StatsStackedBarChart } from "./StatsStackedBarChart";

interface TollAreaStatisticsTableProps {
  data: TollAreaStatistics[];
  totalData?: TollAreaStatistics;
  month: string;
  year: string;
}

function getTollAreaChartLabel(
  data: TollAreaStatistics[],
  row: TollAreaStatistics
): string {
  const isDuplicateName =
    data.filter((item) => item.tollAreaName === row.tollAreaName).length > 1;
  if (isDuplicateName && row.wardName) {
    return `${row.tollAreaName} (${row.wardName})`;
  }
  return row.tollAreaName;
}
export const TollAreaStatisticsTable: React.FC<
  TollAreaStatisticsTableProps
> = ({ data, totalData, month, year }) => {
  const [paymentPie, setPaymentPie] = useState<
    { x: string; y: number }[] | undefined
  >([]);
  const [invoicePie, setInvoicePie] = useState<
    { x: string; y: number }[] | undefined
  >([]);
  const [productionBar, setProductionBar] = useState<
    { x: string; y: number }[]
  >([]);
  const [paymentBar, setPaymentBar] = useState<
    { x: string; y1: number; y2: number }[]
  >([]);

  useEffect(() => {
    if (totalData) {
      setPaymentPie(undefined);
      setInvoicePie(undefined);
      setTimeout(() => {
        setPaymentPie([
          {
            x: "Tổng tiền đã thu",
            y: Number((totalData.daThu / 1000000).toFixed(2)),
          },
          {
            x: "Tổng tiền chưa thu",
            y: Number((totalData.chuaThu / 1000000).toFixed(2)),
          },
        ]);
        setInvoicePie([
          { x: "Số hoá đơn đã thu", y: totalData.soHoaDonDaThu },
          { x: "Số hoá đơn chưa thu", y: totalData.soHoaDonChuaThu },
        ]);
      }, 50);
    }
  }, [totalData]);

  useEffect(() => {
    if (data.length > 0) {
      const _production = data.map((row) => ({
        x: getTollAreaChartLabel(data, row),
        y: row.tongLuongNuoc,
      }));
      _production.sort((a, b) => a.y - b.y);
      setProductionBar(_production);

      const _payment = data.map((row) => {
        const total = row.daThu + row.chuaThu;
        return {
          x: `${getTollAreaChartLabel(data, row)} (${formatCurrencyPipeText(
            row.daThu / 1000000,
            2
          )}M/${formatCurrencyPipeText(total / 1000000, 2)}M)`,
          y1: Math.round((row.daThu / total) * 1000) / 10,
          y2: Math.round((row.chuaThu / total) * 1000) / 10,
          total: total,
          production: row.tongLuongNuoc,
        };
      });
      _payment.sort((a, b) => a.production - b.production);
      setPaymentBar(_payment);
    }
  }, [data]);

  const calculateTotalPercentage = (total?: TollAreaStatistics) => {
    if (!total) return "0";
    if (total.tongPhaiThu === 0) return "0";
    return `${Math.round((total.daThu / total.tongPhaiThu) * 1000) / 10}`;
  };

  return (
    <Row gutter={24}>
      <Col span={12}>
        <SummaryCards
          totalCustomers={totalData?.tongSoKhachHang ?? 0}
          totalAppUsers={totalData?.soLuongCaiApp ?? 0}
          totalDeposits={totalData?.tongTienDaDong ?? 0}
          totalBills={totalData?.soLuongHoaDon ?? 0}
          selectedMonth={month}
          selectedYear={year}
        />
        <div className="mb-6"></div>
        <Card bordered>
          <p className="text-center text-lg mb-2 font-bold">
            Trạng thái thu phí {month}/{year}:{" "}
            {calculateTotalPercentage(totalData)}(%)
          </p>
          <StatsStackedBarChart
            data={paymentBar}
            title=""
            rowHeight={30}
            template1={(args: any) => {
              if (args.point.y < 5) return <div></div>;
              return <div style={{ color: "white" }}>{args.point.y}%</div>;
            }}
            template2={(args: any) => {
              if (args.point.y < 5) return <div></div>;
              return <div style={{ color: "#ff5175" }}>{args.point.y}%</div>;
            }}
            name={{ y1: "Đã thu", y2: "Chưa thu" }}
            primaryxAxis={{
              valueType: "Category",
              maximumLabelWidth: 150,
              enableTrim: true,
            }}
            primaryyAxis={{ minimum: 0, maximum: 100 }}
            palettes={["#346888", "#9dc6e0"]}
          />
        </Card>
      </Col>
      <Col span={12}>
        <Row gutter={24}>
          <Col span={12}>
            <Card bordered>
              <StatsPieChart
                data={paymentPie}
                height="425"
                template={(args: any) => {
                  const percentage =
                    ((args.point.y * 1000000) / (totalData?.tongPhaiThu ?? 1)) *
                    100;
                  return (
                    <div id="templateWrap">
                      <div>{formatCurrencyPipeText(percentage)}%</div>
                    </div>
                  );
                }}
                unit="M"
                palettes={["#004c6d", "#9dc6e0"]}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card bordered>
              <StatsPieChart
                data={invoicePie}
                height="425"
                template={(args: any) => {
                  const percentage =
                    (args.point.y / (totalData?.soLuongHoaDon ?? 1)) * 100;
                  return (
                    <div id="templateWrap">
                      <div>{formatCurrencyPipeText(percentage)}%</div>
                    </div>
                  );
                }}
                unit="HĐ"
                palettes={["#346888", "#c1e7ff"]}
              />
            </Card>
          </Col>
        </Row>
        <div className="mb-6"></div>
        <Card bordered>
          <p className="text-center text-lg mb-2 font-bold">
            Sản lượng tiêu thụ trên khu vực {month}/{year}. Tổng:{" "}
            {totalData?.tongLuongNuoc} (m3)
          </p>
          <div>
            <StatsBarChart
              data={productionBar}
              title=""
              rowHeight={30}
              primaryxAxis={{
                valueType: "Category",
                maximumLabelWidth: 150,
                enableTrim: true,
              }}
              primaryyAxis={{}}
              palettes={["#346888"]}
            />
          </div>
        </Card>
      </Col>
    </Row>
  );
};
